import React, { useState, useEffect, } from 'react';
import { Modal, Form, Input, Upload, Button, message, Select, DatePicker, Row, Col } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import { OneKlicksFileSDK } from '../../SDK/OneKlicksFileSDK';
import { MapContainer, TileLayer, Marker, useMapEvents, Circle } from 'react-leaflet';
import { useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import moment from 'moment';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const { Option } = Select;
const { TextArea } = Input;

// Map click handler component
function MapClickHandler({ onLocationSelect }) {
    useMapEvents({
        click: (e) => {
            onLocationSelect([e.latlng.lat, e.latlng.lng]);
        },
    });
    return null;
}

const AddBannerModal = ({ visible, onCancel, onSuccess, isEdit = false, editData = null }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [imageId, setImageId] = useState('');
    const [manualImage, setManualImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState([51.505, -0.09]); // Default to London
    const [showMap, setShowMap] = useState(false);
    const [address, setAddress] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [targetRadius, setTargetRadius] = useState(10); // Default 10km radius
    const searchRef = useRef();
    const searchContainerRef = useRef();
    const [bannersImageSize, setBannersImageSize] = useState('');

    // Banner type options
    const bannerTypes = [
        { value: 'hero', label: 'Hero Banner', size: '1920x1080' },
        { value: 'doctor_listing', label: 'Doctor Listing Banner', size: '920x100' },
        { value: 'hospital_listing', label: 'Hospital Listing Banner', size: '920x100' },
        { value: 'right_sidebar', label: 'Right Sidebar Banner', size: '510x400' },
        { value: 'home_page', label: 'Home Page Banner', size: '1920x1080' },
        { value: 'search_page', label: 'Search Page Banner', size: '920x100' },
        { value: 'category_page', label: 'Category Page Banner', size: '1920x1080' },
        { value: 'popup', label: 'Popup Banner', size: '512x512' },
        { value: 'notification', label: 'Notification Banner', size: '920x100' }
    ];

    // Location options
    const locationOptions = [
        { value: 'global', label: 'Global' },
        { value: 'specific_location', label: 'Specific Location' },
        { value: 'multiple_locations', label: 'Multiple Locations' }
    ];

    const fetchAddress = async (lat, lng) => {
        if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;

        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`,
                { headers: { 'Accept': 'application/json' } }
            );

            if (res.status === 429) {
                setAddress('Rate limit exceeded. Please wait...');
                return;
            }

            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

            const data = await res.json();
            if (data && data.display_name) {
                setAddress(data.display_name);
            } else {
                setAddress('Address not found');
            }
        } catch (err) {
            console.error('Error fetching address:', err);
            setAddress('Error fetching address');
        }
    };

    const handleLocationSelect = (coords) => {
        setSelectedLocation(coords);
        form.setFieldsValue({
            latitude: coords[0],
            longitude: coords[1]
        });
        fetchAddress(coords[0], coords[1]);
    };

    const searchPlace = async () => {
        const query = searchRef.current.value;
        if (!query) return;
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
            );
            if (!res.ok) throw new Error('Network response was not ok');
            const data = await res.json();
            if (data && data.length > 0) {
                const lat = parseFloat(data[0].lat);
                const lng = parseFloat(data[0].lon);
                const coords = [lat, lng];
                setSelectedLocation(coords);
                form.setFieldsValue({
                    latitude: lat,
                    longitude: lng
                });
                setAddress(data[0].display_name);
                setShowSuggestions(false);
                setShowMap(true); // Show map when location is found
            } else {
                message.error('Place not found');
            }
        } catch (error) {
            console.error('Search error:', error);
            message.error('Error searching for place. Please check your internet connection.');
        }
    };

    const getSearchSuggestions = async (query) => {
        if (!query || query.length < 2) {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`
            );
            const data = await res.json();
            setSearchSuggestions(data);
            setShowSuggestions(data.length > 0);
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setSearchSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const selectSuggestion = (suggestion) => {
        const lat = parseFloat(suggestion.lat);
        const lng = parseFloat(suggestion.lon);
        const coords = [lat, lng];
        setSelectedLocation(coords);
        form.setFieldsValue({
            latitude: lat,
            longitude: lng
        });
        setAddress(suggestion.display_name);
        searchRef.current.value = suggestion.display_name;
        setShowSuggestions(false);
        setSearchSuggestions([]);
        setShowMap(true); // Show map when location is selected
        message.success(`Location selected: ${suggestion.display_name.split(',')[0]}`);
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            message.loading('Getting your current location...', 0);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    const coords = [lat, lng];
                    setSelectedLocation(coords);
                    form.setFieldsValue({
                        latitude: lat,
                        longitude: lng
                    });
                    fetchAddress(lat, lng);
                    setShowMap(true); // Show map when current location is obtained
                    message.destroy();
                    message.success('Current location obtained successfully!');
                },
                () => {
                    message.destroy();
                    message.error('Failed to get current location');
                }
            );
        } else {
            message.error('Geolocation not supported by this browser.');
        }
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Auto-center map when location changes
    useEffect(() => {
        if (showMap && selectedLocation) {
            // Force map to re-center on new location
            const mapElement = document.querySelector('.leaflet-container');
            if (mapElement) {
                // Small delay to ensure map is rendered
                setTimeout(() => {
                    const map = mapElement._leaflet_map;
                    if (map) {
                        map.setView(selectedLocation, 13);
                    }
                }, 100);
            }
        }
    }, [selectedLocation, showMap]);

    const handleSubmit = async (values) => {
        console.log("isEdit", isEdit);
        console.log('values', values);
        console.log('imageId', imageId);
        console.log('imageUrl', imageUrl);
        try {
            setLoading(true);

            if (!imageId) {
                message.error('Please upload an image');
                return;
            }

            const tableSchema = '';
            const tableName = 'banners';

            const bannerData = {
                banner_name: values.name,
                banner_type: values.bannerType,
                imageId: imageId,
                imageUrl: imageUrl,
                redirect_link: values.redirectLink || '',
                redirect_type: values.redirectType || 'external',
                location_type: values.locationType || 'global',
                latitude: values.latitude || null,
                longitude: values.longitude || null,
                target_radius: targetRadius,
                target_audience: values.targetAudience || '',
                priority: values.priority || 1,
                start_date: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
                end_date: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
                status: values.status || 'active',
                description: values.description || '',
                banner_dimensions: values.bannerDimensions || '',
                tracking_enabled: values.trackingEnabled || false,
                max_clicks: values.maxClicks || 0,
                clicks: 0,
                impressions: 0
            };

            let response;

            if (isEdit) {
                console.log("editData", editData);
                const condition = { id: editData.id }
                response = await oneKlicks.update(tableSchema, tableName, bannerData, condition);
                console.log("response", response);
            } else {
                response = await oneKlicks.create(tableSchema, tableName, bannerData, {});
            }

            if (response.success) {
                message.success('Banner added successfully');
                form.resetFields();
                setImageUrl('');
                setImageId('');
                setSelectedLocation([51.505, -0.09]);
                setAddress('');
                setSearchSuggestions([]);
                setShowSuggestions(false);
                setTargetRadius(10);
                onSuccess();
            } else {
                message.error('Failed to add banner');
            }
        } catch (error) {
            console.error('Error adding banner:', error);
            message.error('Failed to add banner');
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        onRemove: () => {
            setImageUrl('');
            setImageId('');
        },
        beforeUpload: async (file) => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                message.error('You can only upload image files!');
                return Upload.LIST_IGNORE;
            }

            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                message.error('Image must be smaller than 2MB!');
                return Upload.LIST_IGNORE;
            }

            try {
                setUploading(true);
                const root = 'banners';
                const status = 'publish';

                const uploadResult = await OneKlicksFileSDK.uploadFile(root, status, file);

                if (uploadResult && uploadResult.fileId) {
                    setImageId(uploadResult.fileId);
                    setImageUrl(URL.createObjectURL(file));
                    message.success('Image uploaded successfully');
                } else {
                    message.error('Failed to upload image');
                    return Upload.LIST_IGNORE;
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                message.error('Failed to upload image');
                return Upload.LIST_IGNORE;
            } finally {
                setUploading(false);
            }

            return false;
        },
        fileList: imageUrl
            ? [
                {
                    uid: '-1',
                    name: 'image.png',
                    status: 'done',
                    url: imageUrl,
                },
            ]
            : [],
        showUploadList: {
            showRemoveIcon: true,
        },
    };

    const handleCancel = () => {
        form.resetFields();
        setImageUrl('');
        setImageId('');
        setSelectedLocation([51.505, -0.09]);
        setAddress('');
        setSearchSuggestions([]);
        setShowSuggestions(false);
        setTargetRadius(10);
        onCancel();
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];

        const bannerType = form.getFieldValue('bannerType');
        const bannerSize = bannerTypes.find(type => type.value === bannerType)?.size;

        // Check image dimensions (width x height) if bannerSize is defined
        if (file && bannerSize) {
            const [expectedWidth, expectedHeight] = bannerSize.split('x').map(Number);

            const img = new window.Image();
            img.onload = function () {
                // if (img.width !== expectedWidth || img.height !== expectedHeight) {
                //     message.error(`Banner image dimensions must be ${bannerSize} pixels`);
                //     alert(`Banner image dimensions must be ${bannerSize} pixels`); // Remove this alert
                //     return;
                // }
            };
            img.src = URL.createObjectURL(file);
        }

        console.log("bannerSize", bannerSize);

        if (file && file.type.startsWith('image/') && file.size <= 2 * 1024 * 1024) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setManualImage(file);
            };
            reader.readAsDataURL(file);
        } else {
            alert('Please upload a valid image (max 2MB).');
        }

        const root = "doctors/banner";
        const status = "public";
        const uploadResult = await OneKlicksFileSDK.uploadFile(root, status, file);

        if (!uploadResult || !uploadResult.fileId) {
            throw new Error('Failed to upload file');
        }

        console.log("uploadResult new", uploadResult);

        const bannerurl = `${uploadResult.file.root}/${uploadResult.file.fileName}`;

        console.log("bannerurl", bannerurl);

        setImageId(uploadResult.fileId);
        setImagePreview(bannerurl);
        setImageUrl(bannerurl);
    };

    const handleBannerTypeChange = (value) => {
        const bannerType = bannerTypes.find(type => type.value === value);
        const bannerSize = bannerType?.size;
        console.log("bannerSize", bannerSize);
        setBannersImageSize(bannerSize);
    };

    return (
        <Modal
            title={isEdit ? "Edit Banner" : "Add New Banner"}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={800}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    name: isEdit ? editData.banner_name : '',
                    status: isEdit ? editData.status : 'active',
                    bannerType: isEdit ? editData.banner_type : 'hero',
                    locationType: isEdit ? editData.location_type : 'global',
                    redirectType: isEdit ? editData.redirect_type : 'external',
                    priority: isEdit ? editData.priority : 1,
                    trackingEnabled: isEdit ? editData.tracking_enabled : false,
                    maxClicks: isEdit ? editData.max_clicks : 0,
                    bannerDimensions: isEdit ? editData.banner_dimensions : '728x90',
                    redirectLink: isEdit ? editData.redirect_link : '',
                    description: isEdit ? editData.description : '',
                    targetAudience: isEdit ? editData.target_audience : '',
                    startDate: isEdit ? moment(editData.start_date) : '',
                    endDate: isEdit ? moment(editData.end_date) : '',
                    latitude: isEdit ? editData.latitude : '',
                    longitude: isEdit ? editData.longitude : ''
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label="Banner Name"
                            rules={[{ required: true, message: 'Please enter banner name' }]}
                        >
                            <Input className='form-control' placeholder="Enter banner name" value={isEdit ? editData.banner_name : ''} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="bannerType"
                            label="Banner Type"
                            rules={[{ required: true, message: 'Please select banner type' }]}
                        >
                            <Select placeholder="Select banner type" className='form-control' style={{ border: '1px solid #d9d9d900', padding: '0px' }} onChange={handleBannerTypeChange} value={isEdit ? editData.banner_type : ''}>
                                {bannerTypes.map(type => (
                                    <Option key={type.value} value={type.value}>{type.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="status"
                            label="Status"
                        >
                            <Select placeholder="Select status" className='form-control' style={{ border: '1px solid #d9d9d900', padding: '0px' }} value={isEdit ? editData.status : ''}>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                                <Option value="draft">Draft</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="priority"
                            label="Priority"
                        >
                            <Select placeholder="Select priority" className='form-control' style={{ border: '1px solid #d9d9d900', padding: '0px' }} value={isEdit ? editData.priority : ''}>
                                <Option value='1'>Low</Option>
                                <Option value='2'>Medium</Option>
                                <Option value='3'>High</Option>
                                <Option value='4'>Critical</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="startDate"
                            label="Start Date"
                        >
                            <DatePicker className='form-control' style={{ width: '100%' }} placeholder="Select start date" value={isEdit ? editData.start_date : ''} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="endDate"
                            label="End Date"
                        >
                            <DatePicker className='form-control' style={{ width: '100%' }} placeholder="Select end date" value={isEdit ? editData.end_date : ''} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="redirectLink"
                            label="Redirect Link"
                        >
                            <Input placeholder="Enter redirect URL" className='form-control' value={isEdit ? editData.redirect_link : ''} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="redirectType"
                            label="Redirect Type"
                        >
                            <Select placeholder="Select redirect type" className='form-control' style={{ padding: '0px' }} value={isEdit ? editData.redirect_type : ''}>
                                <Option value="external">External Link</Option>
                                <Option value="internal">Internal Page</Option>
                                <Option value="doctor">Doctor Profile</Option>
                                <Option value="hospital">Hospital Profile</Option>
                                <Option value="pharmacy">Pharmacy</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="description"
                    label="Description"
                >
                    <TextArea className='form-control' rows={3} placeholder="Enter banner description" value={isEdit ? editData.description : ''} />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="targetAudience"
                            label="Target Audience"
                        >
                            <Input className='form-control' placeholder="e.g., All users, Premium users, Specific age group" value={isEdit ? editData.target_audience : ''} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="bannerDimensions"
                            label="Banner Dimensions"
                        >
                            <Select placeholder="Select dimensions" className='form-control' style={{ border: '1px solid #d9d9d900', padding: '0px' }} value={isEdit ? editData.banner_dimensions : ''}>
                                <Option value="728x90">728x90 (Leaderboard)</Option>
                                <Option value="300x250">300x250 (Medium Rectangle)</Option>
                                <Option value="300x600">300x600 (Half Page)</Option>
                                <Option value="320x50">320x50 (Mobile Banner)</Option>
                                <Option value="970x90">970x90 (Large Leaderboard)</Option>
                                <Option value="970x250">970x250 (Billboard)</Option>
                                <Option value="custom">Custom Size</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="trackingEnabled"
                            label="Enable Tracking"
                            valuePropName="checked"
                        >
                            <Select placeholder="Select tracking option" className='form-control' style={{ border: '1px solid #d9d9d900', padding: '0px' }} value={isEdit ? editData.tracking_enabled : ''}>
                                <Option value={true}>Yes</Option>
                                <Option value={false}>No</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="maxClicks"
                            label="Max Clicks (0 = unlimited)"
                        >
                            <Input type="number" className='form-control' placeholder="0" min="0" value={isEdit ? editData.max_clicks : ''} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="locationType"
                    label="Location Type"
                >
                    <Select
                        placeholder="Select location type"
                        className='form-control'
                        style={{ border: '1px solid #d9d9d900', padding: '0px' }}
                        onChange={(value) => {
                            if (value === 'specific_location') {
                                setShowMap(true);
                            } else {
                                setShowMap(false);
                            }
                        }}
                        value={isEdit ? editData.location_type : ''}
                    >
                        {locationOptions.map(option => (
                            <Option key={option.value} value={option.value}>{option.label}</Option>
                        ))}
                    </Select>
                </Form.Item>

                {showMap && (
                    <Form.Item label="Select Location">
                        {selectedLocation && (
                            <div style={{
                                marginBottom: '10px',
                                padding: '8px',
                                backgroundColor: '#e6f7ff',
                                border: '1px solid #91d5ff',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}>
                                🎯 <strong>Target Point:</strong> {selectedLocation[0].toFixed(6)}, {selectedLocation[1].toFixed(6)}
                            </div>
                        )}
                        <div style={{ marginBottom: '10px' }}>
                            <div gutter={12}>
                                <div span={12}>
                                    <div ref={searchContainerRef} style={{ position: 'relative' }}>
                                        <Input
                                            ref={searchRef}
                                            className='form-control'
                                            placeholder="Search for a place (e.g., hyd, mumbai, delhi)"
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                getSearchSuggestions(value);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    searchPlace();
                                                    e.preventDefault();
                                                }
                                            }}
                                            onFocus={() => {
                                                if (searchSuggestions.length > 0) {
                                                    setShowSuggestions(true);
                                                }
                                            }}
                                        />
                                        {showSuggestions && searchSuggestions.length > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                right: 0,
                                                backgroundColor: 'white',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                zIndex: 1000,
                                                maxHeight: '200px',
                                                overflowY: 'auto'
                                            }}>
                                                {searchSuggestions.map((suggestion, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '8px 12px',
                                                            cursor: 'pointer',
                                                            borderBottom: index < searchSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                            fontSize: '14px'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#f5f5f5';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = 'white';
                                                        }}
                                                        onClick={() => selectSuggestion(suggestion)}
                                                    >
                                                        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                                            {suggestion.name || suggestion.display_name.split(',')[0]}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                            {suggestion.display_name}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='d-flex'>
                                    <div className='col-lg-6 col-md-6 col-sm-12 col-xs-12' style={{ padding: '0px' }}>
                                        <Form.Item label="Target Radius" style={{ marginBottom: 0 }}>
                                            <Select
                                                value={targetRadius}
                                                onChange={setTargetRadius}
                                                className='form-control'
                                                style={{ border: '1px solid #d9d9d900', padding: '0px' }}
                                            >
                                                <Option value={5}>5 km</Option>
                                                <Option value={10}>10 km</Option>
                                                <Option value={15}>15 km</Option>
                                                <Option value={20}>20 km</Option>
                                                <Option value={25}>25 km</Option>
                                                <Option value={30}>30 km</Option>
                                                <Option value={50}>50 km</Option>
                                            </Select>
                                        </Form.Item>
                                    </div>
                                    <div className='col-lg-3 col-md-3 col-sm-6 col-xs-6' style={{ padding: '0px' }}>
                                        <Button style={{ width: '100%' }} onClick={searchPlace} type="primary">
                                            Search
                                        </Button>
                                    </div>
                                    <div className='col-lg-3 col-md-3 col-sm-6 col-xs-6' style={{ padding: '0px' }}>
                                        <Button style={{ width: '100%' }} onClick={getCurrentLocation}>
                                            📍 Current
                                        </Button>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div style={{ height: '300px', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
                            <MapContainer
                                key={`${selectedLocation[0]}-${selectedLocation[1]}-${targetRadius}`}
                                center={selectedLocation}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <Marker
                                    position={selectedLocation}
                                    icon={L.divIcon({
                                        className: 'custom-marker',
                                        html: '<div style="background-color: #ff0000; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                                        iconSize: [20, 20],
                                        iconAnchor: [10, 10]
                                    })}
                                />
                                <Circle
                                    center={selectedLocation}
                                    radius={targetRadius * 1000} // Convert km to meters
                                    pathOptions={{
                                        color: 'red',
                                        fillColor: '#ff0000',
                                        fillOpacity: 0.1,
                                        weight: 2
                                    }}
                                />
                                <MapClickHandler onLocationSelect={handleLocationSelect} />
                            </MapContainer>
                        </div>
                        {address && (
                            <div style={{ marginTop: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                                <strong>Address:</strong> {address}
                                <br />
                                <strong>Target Radius:</strong> {targetRadius} km
                            </div>
                        )}
                        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                            Click on the map to select location or use search above. Red circle shows target radius.
                        </p>
                    </Form.Item>
                )}

                <Row gutter={16} style={{ display: showMap ? 'flex' : 'none' }}>
                    <Col span={12}>
                        <Form.Item
                            name="latitude"
                            label="Latitude"
                        >
                            <Input className='form-control' placeholder="Latitude" readOnly value={isEdit ? editData.latitude : ''} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="longitude"
                            label="Longitude"
                        >
                            <Input className='form-control' placeholder="Longitude" readOnly value={isEdit ? editData.longitude : ''} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label={`Banner Image (Dimensions: ${bannersImageSize})`}
                    required
                >
                    <input type="file" className='form-control' accept="image/*" onChange={handleImageChange} />
                    {imageUrl && (
                        <div style={{ marginTop: '8px' }}>
                            <img src={process.env.REACT_APP_API_BASE_LIVE_URL + imageUrl} alt="Banner" style={{ maxWidth: '100%', maxHeight: '200px' }} />
                        </div>
                    )}
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                        Upload an image for the banner (max 2MB, JPG, PNG, GIF)
                    </p>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {isEdit ? "Update Banner" : "Add Banner"}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddBannerModal;
